/**
 * Regions: the named provinces that give the map its labels ("Dornmark", "Zlatovina Heights").
 * Each faction's starting territory is split into ≈ one region per 1.5 of its cities. Region seats
 * are that faction's cities picked by farthest-point sampling (capital first); regions grow from
 * the seats with the same terrain/river costs as territories, never crossing a faction border, so
 * provinces nest inside nations and their boundaries follow rivers and ridges. Contiguous by
 * construction. Names use the owning faction's style; the label anchor is the member hex closest
 * to the region's centroid, avoiding city hexes where possible.
 */
import type { City, GameMap } from '../core/types';
import { Terrain } from '../core/types';
import { isLand, type MapContext } from './context';
import type { FactionPlan, NameBook } from './names';
import { growRegions, type StepCost } from './paths';
import { borderCost } from './territories';

/** Cities per region. */
const CITIES_PER_REGION = 1.5;

export function carveRegions(
  ctx: MapContext,
  owner: Uint8Array,
  cities: readonly City[],
  plans: readonly FactionPlan[],
  names: NameBook,
): { region: Int16Array; regions: GameMap['regions'] } {
  const { grid, n, terrain, ux, uy } = ctx;
  const seats: number[] = [];
  const seatFaction: number[] = [];
  for (let f = 0; f < plans.length; f++) {
    const own = cities.filter((c) => owner[c.hex] === f);
    own.sort((a, b) => Number(b.capitalOf === f) - Number(a.capitalOf === f) || a.id - b.id);
    const want = Math.max(1, Math.round(own.length / CITIES_PER_REGION));
    const chosen = [own[0].hex];
    while (chosen.length < want) {
      let best = -1;
      let bestGap = -1;
      for (const c of own) {
        const gap = Math.min(...chosen.map((s) => grid.distance(s, c.hex)));
        if (gap > bestGap) {
          bestGap = gap;
          best = c.hex;
        }
      }
      if (bestGap <= 0) break;
      chosen.push(best);
    }
    for (const s of chosen) {
      seats.push(s);
      seatFaction.push(f);
    }
  }

  const land = new Uint8Array(n);
  for (let h = 0; h < n; h++) land[h] = isLand(terrain[h]) ? 1 : 0;
  const terrainCost = borderCost(ctx);
  const cost: StepCost = (from, to, dir) =>
    owner[from] === owner[to] ? terrainCost(from, to, dir) : Infinity;
  const { label } = growRegions(grid, seats, new Array<number>(seats.length).fill(1), cost, land);

  const region = new Int16Array(n).fill(-1);
  const members: number[][] = seats.map(() => []);
  for (let h = 0; h < n; h++) {
    if (label[h] < 0) continue;
    region[h] = label[h];
    members[label[h]].push(h);
  }
  const nearCity = new Uint8Array(n);
  for (const c of cities) {
    nearCity[c.hex] = 2;
    grid.forNeighbours(c.hex, (m) => {
      nearCity[m] = Math.max(nearCity[m], 1);
    });
  }

  const regions = members.map((hexes, id) => {
    let cx = 0;
    let cy = 0;
    const count = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (const h of hexes) {
      cx += ux[h];
      cy += uy[h];
      count[terrain[h]]++;
    }
    cx /= hexes.length;
    cy /= hexes.length;
    let anchor = hexes[0];
    let best = Infinity;
    for (const h of hexes) {
      const d = (ux[h] - cx) * (ux[h] - cx) + (uy[h] - cy) * (uy[h] - cy) + 3 * nearCity[h];
      if (d < best) {
        best = d;
        anchor = h;
      }
    }
    const size = hexes.length;
    let epithet: string | null = null;
    if ((count[Terrain.Mountains] + count[Terrain.Hills]) / size > 0.45) epithet = 'Heights';
    else if (count[Terrain.Forest] / size > 0.4) epithet = 'Woods';
    else if (count[Terrain.Marsh] / size > 0.15) epithet = 'Fens';
    return { id, name: names.region(plans[seatFaction[id]].style, epithet), hex: anchor };
  });
  return { region, regions };
}
